import React, { useState, useMemo } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useData } from '../../hooks/useData';
import { useVendorQuotes } from '../../hooks/useVendorQuotes';
import {
  Check,
  Warning,
  Calendar,
  Clock,
  CurrencyCircleDollar,
  WarningCircle,
} from '@phosphor-icons/react';
import { STATUS_LABELS as QUOTE_STATUS_LABELS } from '../../constants/quoteStatus';
import modalStyles from '../../styles/modals.module.css';
import styles from './QuoteComparisonModal.module.css';

/**
 * QuoteComparisonModal - Side-by-side quote comparison
 *
 * Features:
 * - Compare unlimited quotes with horizontal scroll
 * - Highlight best price, fastest lead time
 * - Show vendor info, price, terms, validity
 * - Allow selecting a quote (triggers cost update)
 */

const QuoteComparisonModal = ({
  onClose,
  // onSubmitRef - reserved for future keyboard handling
  itemId, // Project item ID to compare quotes for
  quotes = [], // Pre-loaded quotes (optional)
  projectId,
}) => {
  const { currentProject } = useApp();
  const effectiveProjectId = projectId || currentProject?.id;

  const [isSelecting, setIsSelecting] = useState(false);

  // Use the vendor quotes hook for selection logic
  const { selectQuote } = useVendorQuotes(effectiveProjectId);

  // Fetch quotes if not provided
  const { data: fetchedQuotes, isLoading: quotesLoading } = useData('vendor_quotes', {
    filter: { project_id: effectiveProjectId },
    enabled: !!effectiveProjectId && quotes.length === 0,
  });

  // Fetch quote items to filter by itemId
  const { data: quoteItems, isLoading: quoteItemsLoading } = useData('vendor_quote_items', {
    enabled: !!effectiveProjectId,
  });

  // Fetch master vendors for names
  const { data: masterVendors, isLoading: vendorsLoading } = useData('master_vendors', {
    enabled: !!effectiveProjectId,
  });

  // Fetch project item details
  const { data: projectItems, isLoading: itemsLoading } = useData('project_items', {
    filter: { project_id: effectiveProjectId },
    enabled: !!effectiveProjectId,
  });

  // Fetch master items for names
  const { data: masterItems, isLoading: masterItemsLoading } = useData('master_items', {
    enabled: !!effectiveProjectId,
  });

  // Create lookup maps
  const masterVendorsMap = useMemo(() => {
    if (!masterVendors) return new Map();
    return new Map(masterVendors.map(v => [v.id, v]));
  }, [masterVendors]);

  const masterItemsMap = useMemo(() => {
    if (!masterItems) return new Map();
    return new Map(masterItems.map(item => [item.id, item]));
  }, [masterItems]);

  // Get the item name
  const itemName = useMemo(() => {
    if (!itemId || !projectItems || !masterItems) return 'פריט';
    const projectItem = projectItems.find(pi => pi.id === itemId);
    if (!projectItem) return 'פריט';
    const masterItem = masterItemsMap.get(projectItem.master_item_id);
    return masterItem?.item_name || 'פריט';
  }, [itemId, projectItems, masterItems, masterItemsMap]);

  // Filter quotes relevant to this item
  const relevantQuotes = useMemo(() => {
    const allQuotes = quotes.length > 0 ? quotes : fetchedQuotes || [];

    if (!itemId || !quoteItems) return allQuotes;

    // Find quote items that include this project item
    const relevantQuoteIds = quoteItems
      .filter(qi => qi.project_item_ids?.includes(itemId))
      .map(qi => qi.quote_id);

    return allQuotes.filter(q => relevantQuoteIds.includes(q.id));
  }, [quotes, fetchedQuotes, itemId, quoteItems]);

  // Calculate effective prices for all quotes (memoized)
  const quotePrices = useMemo(() => {
    const prices = new Map();
    relevantQuotes.forEach(quote => {
      const lineItemsTotal = quoteItems
        ? quoteItems
            .filter(qi => qi.quote_id === quote.id && qi.line_price)
            .reduce((sum, qi) => sum + parseFloat(qi.line_price || 0), 0) || null
        : null;
      const totalPrice = quote.total_price ? parseFloat(quote.total_price) : null;

      let displayPrice = null;
      let source = null;
      let hasMismatch = false;

      if (totalPrice !== null) {
        displayPrice = totalPrice;
        source = 'total';
        hasMismatch = lineItemsTotal !== null && Math.abs(totalPrice - lineItemsTotal) > 0.01;
      } else if (lineItemsTotal !== null && lineItemsTotal > 0) {
        displayPrice = lineItemsTotal;
        source = 'calculated';
      }

      prices.set(quote.id, { displayPrice, lineItemsTotal, hasMismatch, source });
    });
    return prices;
  }, [relevantQuotes, quoteItems]);

  // Calculate best values for highlighting
  const bestValues = useMemo(() => {
    if (relevantQuotes.length === 0) return {};

    const prices = relevantQuotes
      .map(q => quotePrices.get(q.id)?.displayPrice)
      .filter(p => p !== null && p !== undefined);

    const leadTimes = relevantQuotes
      .filter(q => q.lead_time_days)
      .map(q => parseInt(q.lead_time_days));

    return {
      lowestPrice: prices.length > 0 ? Math.min(...prices) : null,
      fastestLeadTime: leadTimes.length > 0 ? Math.min(...leadTimes) : null,
    };
  }, [relevantQuotes, quotePrices]);

  // Check if quote is expired
  const isExpired = quote => {
    if (!quote.validity_date) return false;
    return new Date(quote.validity_date) < new Date();
  };

  // Format date
  const formatDate = dateStr => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('he-IL');
  };

  // Format price
  const formatPrice = amount => {
    if (!amount && amount !== 0) return '-';
    return `₪${Number(amount).toLocaleString('he-IL')}`;
  };

  // Handle quote selection
  const handleSelectQuote = async quoteId => {
    setIsSelecting(true);
    try {
      await selectQuote(quoteId);
      onClose();
    } catch (error) {
      console.error('Error selecting quote:', error);
      // Error is already shown by the hook
    } finally {
      setIsSelecting(false);
    }
  };

  const isLoading =
    quotesLoading || quoteItemsLoading || vendorsLoading || itemsLoading || masterItemsLoading;

  return (
    <div
      className={`${modalStyles.modal} ${modalStyles.modalLarge}`}
      dir="rtl"
      onClick={e => e.stopPropagation()}
    >
      {/* Header */}
      <div className={modalStyles.header}>
        <h2 className={modalStyles.headerTitle}>השוואת הצעות מחיר</h2>
        <p className={modalStyles.headerSubtitle}>
          {itemName} - {relevantQuotes.length} הצעות
        </p>
      </div>

      {/* Content */}
      <div className={modalStyles.content}>
        {isLoading ? (
          <div className={modalStyles.loading}>טוען...</div>
        ) : relevantQuotes.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <CurrencyCircleDollar size={48} />
            </div>
            <div className={styles.emptyTitle}>אין הצעות מחיר</div>
            <div className={styles.emptyDescription}>לא נמצאו הצעות מחיר לפריט זה</div>
          </div>
        ) : (
          <div className={styles.comparisonContainer}>
            <div className={styles.comparisonScroll}>
              {relevantQuotes.map(quote => {
                const vendor = masterVendorsMap.get(quote.master_vendor_id);
                const expired = isExpired(quote);
                const priceInfo = quotePrices.get(quote.id) || {};
                const isBestPrice =
                  priceInfo.displayPrice !== null &&
                  priceInfo.displayPrice === bestValues.lowestPrice;
                const isFastest =
                  quote.lead_time_days &&
                  parseInt(quote.lead_time_days) === bestValues.fastestLeadTime;
                const isSelected = quote.status === 'selected';

                return (
                  <div
                    key={quote.id}
                    className={`${styles.quoteCard} ${isSelected ? styles.selectedCard : ''} ${expired ? styles.expiredCard : ''}`}
                  >
                    {/* Status Badge */}
                    {isSelected && (
                      <div className={styles.selectedBadge}>
                        <Check size={14} />
                        נבחר
                      </div>
                    )}
                    {expired && !isSelected && (
                      <div className={styles.expiredBadge}>
                        <Warning size={14} />
                        פג תוקף
                      </div>
                    )}

                    {/* Vendor Info */}
                    <div className={styles.vendorSection}>
                      <div className={styles.vendorName}>{vendor?.name || 'ספק לא ידוע'}</div>
                      <div className={styles.quoteStatus}>
                        {QUOTE_STATUS_LABELS[quote.status] || quote.status}
                      </div>
                    </div>

                    {/* Price */}
                    <div className={styles.priceSection}>
                      <div
                        className={`${styles.priceValue} ${isBestPrice ? styles.bestValue : ''}`}
                      >
                        {formatPrice(priceInfo.displayPrice)}
                        {isBestPrice && <span className={styles.bestBadge}>הזול ביותר</span>}
                      </div>
                      {priceInfo.source === 'calculated' && (
                        <div className={styles.priceSource}>מחושב מפריטים</div>
                      )}
                      {priceInfo.hasMismatch && (
                        <div className={styles.priceMismatch}>
                          <WarningCircle size={14} />
                          <span>סכום פריטים: {formatPrice(priceInfo.lineItemsTotal)}</span>
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className={styles.detailsSection}>
                      <div className={styles.detailRow}>
                        <Clock size={16} className={styles.detailIcon} />
                        <span className={styles.detailLabel}>זמן אספקה:</span>
                        <span
                          className={`${styles.detailValue} ${isFastest ? styles.bestValue : ''}`}
                        >
                          {quote.lead_time_days ? `${quote.lead_time_days} ימים` : '-'}
                          {isFastest && <span className={styles.bestBadge}>המהיר ביותר</span>}
                        </span>
                      </div>
                      <div className={styles.detailRow}>
                        <Calendar size={16} className={styles.detailIcon} />
                        <span className={styles.detailLabel}>תוקף:</span>
                        <span
                          className={`${styles.detailValue} ${expired ? styles.expiredValue : ''}`}
                        >
                          {formatDate(quote.validity_date)}
                        </span>
                      </div>
                      {quote.payment_terms && (
                        <div className={styles.detailRow}>
                          <CurrencyCircleDollar size={16} className={styles.detailIcon} />
                          <span className={styles.detailLabel}>תנאי תשלום:</span>
                          <span className={styles.detailValue}>{quote.payment_terms}</span>
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    {quote.notes && (
                      <div className={styles.notesSection}>
                        <div className={styles.notesLabel}>הערות:</div>
                        <div className={styles.notesContent}>{quote.notes}</div>
                      </div>
                    )}

                    {/* Action Button */}
                    <div className={styles.actionSection}>
                      {isSelected ? (
                        <button className={styles.selectedButton} disabled>
                          <Check size={16} />
                          הצעה נבחרת
                        </button>
                      ) : (
                        <button
                          className={styles.selectButton}
                          onClick={() => handleSelectQuote(quote.id)}
                          disabled={isSelecting || expired}
                        >
                          בחר הצעה זו
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className={modalStyles.footerEnd}>
        <button type="button" className={modalStyles.cancelButton} onClick={onClose}>
          סגור
        </button>
      </div>
    </div>
  );
};

export default QuoteComparisonModal;
